import { motion, useMotionValue, useTransform, animate } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { formatClock, MAX_CLIP_SECONDS } from "@mdj/shared";

const LENGTHS = [5, 8, 10, 15, 20];

// Drag a fixed-length window along the full duration of the video. Length is
// chosen from chips (or nudged), start is where the window sits. That is the
// whole interaction — no dual-thumb slider to fumble with on a phone.
export function Trimmer({
  duration, start, length, onChange,
}: { duration: number; start: number; length: number; onChange: (start: number, length: number) => void }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [trackW, setTrackW] = useState(0);
  const x = useMotionValue(0);

  useEffect(() => {
    const el = trackRef.current; if (!el) return;
    const ro = new ResizeObserver(() => setTrackW(el.clientWidth - 2));
    ro.observe(el); setTrackW(el.clientWidth - 2);
    return () => ro.disconnect();
  }, []);

  const pxPerSec = duration > 0 && trackW > 0 ? trackW / duration : 0;
  const winW = Math.max(56, length * pxPerSec);
  const maxX = Math.max(0, trackW - winW);

  // Keep the handle where `start` says when the track resizes or length changes.
  useEffect(() => {
    if (!pxPerSec) return;
    const target = Math.min(maxX, start * pxPerSec);
    if (Math.abs(x.get() - target) > 0.5) animate(x, target, { type: "spring", stiffness: 400, damping: 40 });
  }, [pxPerSec, maxX, start, x]);

  const liveStart = useTransform(x, (v) => (pxPerSec ? v / pxPerSec : 0));
  const [shown, setShown] = useState(start);
  useEffect(() => liveStart.on("change", (v) => setShown(v)), [liveStart]);

  const setLength = (l: number) => onChange(Math.min(start, Math.max(0, duration - l)), l);

  return (
    <div className="trimmer">
      <div className="readout">
        <div><small>Start</small><b>{formatClock(shown)}</b></div>
        <div style={{ textAlign: "center" }}><small>Length</small><b>{length}s</b></div>
        <div style={{ textAlign: "right" }}><small>Stop</small><b>{formatClock(Math.min(duration, shown + length))}</b></div>
      </div>
      <div className="track" ref={trackRef}>
        <motion.div
          className="window" style={{ x, width: winW }}
          drag="x" dragConstraints={{ left: 0, right: maxX }} dragElastic={0} dragMomentum={false}
          onDragEnd={() => onChange(Math.round((x.get() / pxPerSec) * 10) / 10, length)}
          whileDrag={{ scale: 1.04 }}
          role="slider" aria-label="Clip start" aria-valuemin={0} aria-valuemax={duration} aria-valuenow={shown}
        >
          {length}s
        </motion.div>
      </div>
      <div className="chips" role="group" aria-label="Clip length">
        {LENGTHS.filter((l) => l <= MAX_CLIP_SECONDS).map((l) => (
          <motion.button key={l} type="button" className={`chip${l === length ? " on" : ""}`} onClick={() => setLength(l)} whileTap={{ scale: 0.94 }}>
            {l}s
          </motion.button>
        ))}
      </div>
    </div>
  );
}
